import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Package, Search, Filter, Clock, CheckCircle, XCircle,
  ChevronRight, Eye, ThumbsUp, ThumbsDown, CreditCard, Loader2, Trash2,
  MapPin, CheckCheck, ShoppingCart, Truck
} from "lucide-react";
import { toast } from "sonner";
import { PaymentModal } from "@/components/enterprise/PaymentModal";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null); // For payment modal
  const [deleteOrder, setDeleteOrder] = useState(null); // For delete confirmation
  const [sendingToAmazon, setSendingToAmazon] = useState(null); // Track which order is being sent
  const [amazonInfoModal, setAmazonInfoModal] = useState(null); // For updating Amazon info

  const token = localStorage.getItem('enterprise_token');
  const user = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    // Check for payment success callback
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success("Peman konplete avèk siksè!");
    }
  }, [searchParams]);

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    fetchOrders();
  }, [token, navigate, statusFilter]);

  const fetchOrders = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/enterprise/orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Erè nan chaje kòmand yo");
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (orderId, action) => {
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/approve`,
        { action, comment: action === "reject" ? "Rejte pa " + user.name : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(action === "approve" ? "Kòmand apwouve!" : "Kòmand rejte");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan aksyon an");
    }
  };

  const handlePayWithMoncash = async (orderId) => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/pay`,
        { method: "moncash" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.redirect_url) {
        toast.success("Redireksyon nan MonCash...");
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan peman an");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDelete = async (orderId, orderNumber) => {
    try {
      const response = await axios.delete(
        `${API}/enterprise/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Kòmand efase");
        fetchOrders();
        setSelectedOrder(null);
        setDeleteOrder(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan efase kòmand la");
    }
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/confirm-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Peman konfime avèk siksè!");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error("Confirm payment error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan konfime peman an");
    }
  };

  const handleMarkComplete = async (orderId, amazonOrderId = "") => {
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/mark-complete`,
        { amazon_order_id: amazonOrderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Kòmand make konplete!");
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error("Mark complete error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan make kòmand konplete");
    }
  };

  // =============== AMAZON PO FUNCTIONS ===============

  const handleSendToAmazon = async (orderId) => {
    setSendingToAmazon(orderId);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/send-to-amazon`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("✅ PO voye bay Amazon avèk siksè!");
        fetchOrders();
        setSelectedOrder(null);
      } else {
        toast.error(`❌ Erè: ${response.data.error || response.data.message}`);
      }
    } catch (error) {
      console.error("Send to Amazon error:", error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message;
      toast.error(`❌ ${typeof errorMsg === 'string' ? errorMsg : "Erè nan voye bay Amazon"}`);
    } finally {
      setSendingToAmazon(null);
    }
  };

  // New function: Add items to Amazon cart directly
  // State for ASIN modal
  const [showAsinModal, setShowAsinModal] = useState(false);
  const [asinOrder, setAsinOrder] = useState(null);
  const [itemAsins, setItemAsins] = useState({});

  const handleAddToAmazonCart = (order) => {
    // Check if order is already purchased
    if (order.status === 'purchased' || order.status === 'PURCHASED') {
      toast.error("⚠️ Kòmand sa a deja achte sou Amazon!");
      return;
    }

    // Get items from order
    const items = order.items || [];

    if (items.length === 0) {
      toast.error("⚠️ Pa gen atik nan kòmand sa a!");
      return;
    }

    // Check if all items have ASIN
    const itemsWithoutAsin = items.filter(item => !item.asin && !item.ASIN);
    if (itemsWithoutAsin.length > 0) {
      // Show modal to add ASINs
      setAsinOrder(order);
      // Initialize itemAsins with existing ASINs
      const existingAsins = {};
      items.forEach((item, index) => {
        existingAsins[index] = item.asin || item.ASIN || '';
      });
      setItemAsins(existingAsins);
      setShowAsinModal(true);
      return;
    }

    // All items have ASIN - proceed with add to cart
    proceedToAmazonCart(order);
  };

  const proceedToAmazonCart = (order) => {
    const items = order.items || [];

    // Build Amazon add-to-cart URL
    // Format: https://www.amazon.com/gp/aws/cart/add.html?ASIN.1={ASIN}&Quantity.1={QTY}
    let cartUrl = "https://www.amazon.com/gp/aws/cart/add.html?";

    const params = items.map((item, index) => {
      const asin = item.asin || item.ASIN;
      const qty = item.quantity || 1;
      return `ASIN.${index + 1}=${asin}&Quantity.${index + 1}=${qty}`;
    });

    cartUrl += params.join("&");

    console.log("Amazon Cart URL:", cartUrl);

    // Open Amazon cart URL in new tab
    const amazonTab = window.open(cartUrl, '_blank');

    if (amazonTab) {
      toast.success("🛒 Ap ajoute atik yo nan Amazon cart...");

      // After 2 seconds, redirect to cart view
      setTimeout(() => {
        try {
          amazonTab.location.href = "https://www.amazon.com/gp/cart/view.html";
        } catch (e) {
          // Cross-origin restriction - just notify user
          console.log("Cannot redirect - cross-origin");
        }
      }, 2000);

      // Update order status to indicate it was sent to Amazon
      markOrderAsSentToAmazon(order.id);
    } else {
      toast.error("⚠️ Navigatè a bloke popup. Tanpri pèmèt popup pou sit sa a.");
    }
  };

  // Save ASINs and proceed
  const handleSaveAsinsAndProceed = async () => {
    if (!asinOrder) return;

    // Check if all ASINs are filled
    const items = asinOrder.items || [];
    const missingAsins = items.filter((_, index) => !itemAsins[index] || itemAsins[index].trim() === '');

    if (missingAsins.length > 0) {
      toast.error("⚠️ Tanpri ranpli tout ASIN yo");
      return;
    }

    // Update order with ASINs
    try {
      const updatedItems = items.map((item, index) => ({
        ...item,
        asin: itemAsins[index].trim().toUpperCase()
      }));

      await axios.put(
        `${API}/enterprise/orders/${asinOrder.id}/update-asins`,
        { items: updatedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Close modal and proceed
      setShowAsinModal(false);

      // Update the order object with new ASINs
      const updatedOrder = { ...asinOrder, items: updatedItems };
      proceedToAmazonCart(updatedOrder);

      // Refresh orders
      fetchOrders();
    } catch (error) {
      console.error("Error updating ASINs:", error);
      toast.error("Erè nan anrejistre ASIN yo");
    }
  };

  // Mark order as sent to Amazon
  const markOrderAsSentToAmazon = async (orderId) => {
    try {
      await axios.put(
        `${API}/enterprise/orders/${orderId}/mark-amazon-sent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOrders();
    } catch (error) {
      console.error("Error marking order as sent:", error);
    }
  };

  // Check if can add to Amazon cart
  const canAddToAmazonCart = (order) => {
    return user.role === 'super_admin' &&
      ['approved', 'paid', 'payment_confirmed'].includes(order.status) &&
      order.status !== 'purchased' &&
      order.status !== 'PURCHASED';
  };

  const handleRetryAmazon = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${orderId}/retry-amazon`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Estati reset. Ou ka voye ankò kounye a.");
        fetchOrders();
      }
    } catch (error) {
      console.error("Retry Amazon error:", error);
      toast.error("Erè nan reset estati");
    }
  };

  const handleUpdateAmazonInfo = async (orderId, amazonOrderId, trackingNumber, trackingCarrier) => {
    try {
      const params = new URLSearchParams();
      if (amazonOrderId) params.append('amazon_order_id', amazonOrderId);
      if (trackingNumber) params.append('tracking_number', trackingNumber);
      if (trackingCarrier) params.append('tracking_carrier', trackingCarrier);

      const response = await axios.put(
        `${API}/enterprise/orders/${orderId}/amazon-info?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Enfòmasyon Amazon aktyalize!");
        fetchOrders();
        setAmazonInfoModal(null);
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error("Update Amazon info error:", error);
      toast.error("Erè nan aktyalize enfòmasyon");
    }
  };

  const canSendToAmazon = (order) => {
    // Only SUPER_ADMIN can send PO to Amazon
    // Order must be paid or payment_confirmed and not already sent
    return user.role === 'super_admin' &&
      ['paid', 'payment_confirmed'].includes(order.status) &&
      !order.amazon_po_sent_at;
  };

  const canRetryAmazon = (order) => {
    return user.role === 'super_admin' && order.status === 'amazon_error';
  };

  const canUpdateAmazonInfo = (order) => {
    return user.role === 'super_admin' &&
      ['sent_to_amazon', 'shipped', 'delivered'].includes(order.status);
  };

  const canConfirmPayment = (order) => {
    // Only SUPER_ADMIN can confirm manual payments (Zelle/Virement)
    const paymentMethod = order.payment?.method;
    const paymentStatus = order.payment?.status;
    return user.role === 'super_admin' &&
      order.status === 'pending_payment' &&
      ['zelle', 'virement'].includes(paymentMethod) &&
      paymentStatus === 'proof_submitted';
  };

  const canMarkComplete = (order) => {
    // Only SUPER_ADMIN can mark paid orders as complete
    return user.role === 'super_admin' && order.status === 'paid';
  };

  const canDelete = (order) => {
    // Admin and Super Admin can delete, but not paid or completed orders
    return (user.role === 'admin' || user.role === 'super_admin') &&
      order.status !== 'paid' && order.status !== 'completed';
  };

  const canPay = (order) => {
    return order.status === 'approved' || order.status === 'pending_payment';
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      pending_payment: "bg-orange-100 text-orange-800",
      paid: "bg-blue-100 text-blue-800",
      payment_confirmed: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-emerald-100 text-emerald-800",
      processing: "bg-purple-100 text-purple-800",
      sent_to_amazon: "bg-indigo-100 text-indigo-800",
      amazon_error: "bg-red-100 text-red-800",
      shipped: "bg-cyan-100 text-cyan-800",
      delivered: "bg-emerald-100 text-emerald-800",
      purchased: "bg-green-100 text-green-800"
    };

    const labels = {
      pending_approval: "An Atant",
      approved: "Prè pou Peye",
      pending_payment: "Ap Tann Peman",
      paid: "Peye",
      payment_confirmed: "Peman Konfime",
      rejected: "Rejte",
      completed: "Konplè",
      processing: "An Kou",
      sent_to_amazon: "Voye bay Amazon",
      amazon_error: "Erè Amazon",
      shipped: "Voye",
      delivered: "Livre",
      purchased: "Achte sou Amazon"
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredOrders = orders.filter(order =>
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Admin pa ka apwouve - sèlman super_admin, manager, finance ka apwouve
  const canApprove = (order) => {
    if (user.role === 'admin' || user.role === 'buyer') {
      return false; // Admin ak Buyer pa ka apwouve
    }
    const currentStep = order.approval_workflow?.current_step;
    return order.status === "pending_approval" &&
      (currentStep === user.role || user.role === 'super_admin');
  };

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-orders">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">Kòmand</h1>
            <p className="text-gray-500">Jere tout kòmand antrepriz la</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Chèche pa nimewo kòmand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-orders"
                />
              </div>

              <div className="flex gap-2 overflow-x-auto">
                {[
                  { value: "all", label: "Tout" },
                  { value: "pending_approval", label: "An Atant" },
                  { value: "approved", label: "Prè pou Peye" },
                  { value: "paid", label: "Peye" },
                  { value: "rejected", label: "Rejte" }
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={statusFilter === option.value ? "default" : "outline"}
                    onClick={() => setStatusFilter(option.value)}
                    className={statusFilter === option.value ? "bg-[#FF9900] text-black hover:bg-[#F08C00]" : ""}
                    data-testid={`filter-${option.value}`}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-semibold text-gray-600">Pa gen kòmand</p>
              <p className="text-gray-400">Kòmanse achte sou Amazon Business</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Card
                key={order.id}
                className={`border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer ${canApprove(order) ? 'border-l-4 border-l-yellow-500' : ''
                  }`}
                onClick={() => setSelectedOrder(order)}
                data-testid={`order-${order.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-[#232F3E]">{order.order_number}</span>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="text-sm text-gray-500">
                        {order.items?.length || 0} pwodwi • Kreye: {new Date(order.created_at).toLocaleDateString('fr-HT')}
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#FF9900]">${order.subtotal?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{(order.total_htg || 0).toLocaleString()} HTG</p>
                      </div>

                      {canApprove(order) && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleApproval(order.id, "approve"); }}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${order.id}`}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={(e) => { e.stopPropagation(); handleApproval(order.id, "reject"); }}
                            data-testid={`reject-${order.id}`}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {canPay(order) && (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setPaymentOrder(order); }}
                          className="bg-[#4ADE80] hover:bg-[#3FCF70] text-black font-bold"
                          data-testid={`pay-${order.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Peye
                        </Button>
                      )}

                      {canDelete(order) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setDeleteOrder(order); }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${order.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}

                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>{selectedOrder.order_number}</CardTitle>
                  {getStatusBadge(selectedOrder.status)}
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-3">Pwodwi yo</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">ASIN: {item.asin} • Kantite: {item.quantity}</p>
                        </div>
                        <p className="font-bold">${item.total?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Detay Pri</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sou-Total Amazon:</span>
                      <span className="font-medium">${(selectedOrder.amazon_base || (selectedOrder.amazon_subtotal - (selectedOrder.amazon_tax || 0) - (selectedOrder.amazon_shipping || 0)) || 0).toFixed(2)}</span>
                    </div>
                    {selectedOrder.amazon_tax > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Taks Amazon:</span>
                        <span className="font-medium">+ ${selectedOrder.amazon_tax.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.amazon_shipping > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Livrezon Amazon:</span>
                        <span className="font-medium">+ ${selectedOrder.amazon_shipping.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t pt-1 mt-1">
                      <span>Total Amazon:</span>
                      <span>${(selectedOrder.amazon_subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#FF9900] pt-1">
                      <span>Frè BVGLINKAMAZON (10%):</span>
                      <span className="font-medium">+ ${(selectedOrder.bvgpromo_fee || 0).toFixed(2)}</span>
                    </div>

                    {selectedOrder.haiti_shipping_fee > 0 && (
                      <div className={`flex justify-between text-sm p-3 rounded-lg border ${selectedOrder.pay_shipping_now ? 'bg-green-50 border-green-100' : 'bg-gray-100 border-gray-200 text-gray-500 italic'}`}>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          <div>
                            <p className="font-bold">Shipping Ayiti ({selectedOrder.haiti_shipping_company})</p>
                            <p className="text-xs">{selectedOrder.haiti_shipping_lbs} lbs</p>
                          </div>
                        </div>
                        <span className="font-bold">
                          {selectedOrder.pay_shipping_now ? `+$${selectedOrder.haiti_shipping_fee.toFixed(2)}` : `(Peye Ayiti: $${selectedOrder.haiti_shipping_fee.toFixed(2)})`}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between font-bold border-t pt-2 text-base">
                      <span>Total USD:</span>
                      <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg text-[#FF9900] bg-[#FF9900]/10 p-2 rounded">
                      <span>Total HTG:</span>
                      <span>{(selectedOrder.total_htg || 0).toLocaleString()} HTG</span>
                    </div>
                    <div className="text-right text-[10px] text-gray-400 italic">
                      Taux: 1 USD = {selectedOrder.exchange_rate} HTG
                    </div>
                  </div>
                </div>

                {/* Approval Workflow */}
                <div>
                  <h3 className="font-semibold mb-3">Workflow Apwobasyon</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedOrder.approval_workflow?.steps?.map((step, index) => (
                      <div key={index} className="flex items-center">
                        <div className={`
                          px-3 py-1 rounded-full text-sm
                          ${step.status === 'approved' ? 'bg-green-100 text-green-800' :
                            step.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-600'}
                        `}>
                          {step.role.toUpperCase()}
                          {step.status === 'approved' && <CheckCircle className="w-4 h-4 inline ml-1" />}
                        </div>
                        {index < selectedOrder.approval_workflow.steps.length - 1 && (
                          <ChevronRight className="w-4 h-4 text-gray-400 mx-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                {canApprove(selectedOrder) && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      onClick={() => handleApproval(selectedOrder.id, "approve")}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Apwouve
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleApproval(selectedOrder.id, "reject")}
                      className="flex-1"
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Rejte
                    </Button>
                  </div>
                )}

                {/* Payment Button - Open Payment Modal */}
                {canPay(selectedOrder) && (
                  <div className="pt-4 border-t">
                    <div className="bg-green-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-green-800 mb-2">Kòmand apwouve! Chwazi metòd peman ou.</p>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Montan:</span>
                        <span className="text-xl font-bold text-[#FF9900]">
                          {(selectedOrder.total_htg || 0).toLocaleString()} HTG
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedOrder(null);
                        setPaymentOrder(selectedOrder);
                      }}
                      className="w-full bg-[#4ADE80] hover:bg-[#3FCF70] text-black py-6 text-lg font-bold"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Chwazi Metòd Peman
                    </Button>
                  </div>
                )}

                {/* Paid Status */}
                {selectedOrder.status === 'paid' && (
                  <div className="pt-4 border-t">
                    <div className="bg-blue-50 p-4 rounded-lg flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Peman Konplete</p>
                        {selectedOrder.payment?.transaction_id && (
                          <p className="text-xs text-blue-600">ID: {selectedOrder.payment.transaction_id}</p>
                        )}
                        {selectedOrder.payment?.method && (
                          <p className="text-xs text-blue-600">Metòd: {selectedOrder.payment.method.toUpperCase()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Shipping Address */}
                {selectedOrder.shipping_address && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#FF9900]" />
                      Adrès Livrezon
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm">
                      <p className="font-medium">{selectedOrder.shipping_address.name}</p>
                      <p>{selectedOrder.shipping_address.street}</p>
                      <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}</p>
                      <p>{selectedOrder.shipping_address.country}</p>
                      {selectedOrder.shipping_address.phone && (
                        <p className="mt-2 text-gray-600">Tel: {selectedOrder.shipping_address.phone}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Confirm Payment Button for SUPER_ADMIN (Zelle/Virement) */}
                {canConfirmPayment(selectedOrder) && (
                  <div className="pt-4 border-t">
                    <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-yellow-800 mb-2 font-medium">
                        Prèv peman resevwa pou {selectedOrder.payment?.method?.toUpperCase()}
                      </p>
                      {selectedOrder.payment?.proof_url && (
                        <a
                          href={`${API}${selectedOrder.payment.proof_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline text-sm"
                        >
                          Wè Prèv Peman
                        </a>
                      )}
                    </div>
                    <Button
                      onClick={() => handleConfirmPayment(selectedOrder.id)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
                      data-testid="confirm-payment-btn"
                    >
                      <CheckCheck className="w-5 h-5 mr-2" />
                      Konfime Peman
                    </Button>
                  </div>
                )}

                {/* Pending Proof Status */}
                {selectedOrder.status === 'pending_payment' &&
                  ['zelle', 'virement'].includes(selectedOrder.payment?.method) &&
                  selectedOrder.payment?.status !== 'proof_submitted' && (
                    <div className="pt-4 border-t">
                      <div className="bg-orange-50 p-4 rounded-lg flex items-center gap-3">
                        <Clock className="w-6 h-6 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-800">Ap Tann Prèv Peman</p>
                          <p className="text-xs text-orange-600">
                            Kliyan la dwe upload prèv peman {selectedOrder.payment?.method?.toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Add to Amazon Cart Button - for paid orders */}
                {canAddToAmazonCart(selectedOrder) && (
                  <div className="pt-4 border-t">
                    <div className="bg-orange-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-orange-800 font-medium">
                        Peman resevwa. Klike bouton an pou ajoute atik yo nan Amazon cart.
                      </p>
                    </div>
                    <Button
                      onClick={() => handleAddToAmazonCart(selectedOrder)}
                      className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-black py-6 text-lg font-bold mb-3"
                      data-testid="add-to-amazon-cart-btn"
                    >
                      <Package className="w-5 h-5 mr-2" />
                      🛒 Ale sou Amazon - Ajoute nan Cart
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      Sa ap ouvri Amazon.com ak tout atik yo nan cart ou
                    </p>
                  </div>
                )}

                {/* Mark as Purchased Button - after sent to Amazon */}
                {selectedOrder.status === 'sent_to_amazon' && user.role === 'super_admin' && (
                  <div className="pt-4 border-t">
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-blue-800 font-medium">
                        Atik yo nan Amazon cart. Lè ou fin peye sou Amazon, klike bouton anba a.
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        const amazonId = prompt("Antre Amazon Order ID (opsyonèl):");
                        try {
                          await axios.put(
                            `${API}/enterprise/orders/${selectedOrder.id}/mark-purchased?amazon_order_id=${amazonId || ""}`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success("✅ Kòmand make kòm achte!");
                          fetchOrders();
                          setSelectedOrder(null);
                        } catch (error) {
                          toast.error("Erè nan aktyalize kòmand");
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
                      data-testid="mark-purchased-btn"
                    >
                      <CheckCheck className="w-5 h-5 mr-2" />
                      ✅ Make Achte sou Amazon
                    </Button>
                  </div>
                )}

                {/* Purchased Status */}
                {selectedOrder.status === 'purchased' && (
                  <div className="pt-4 border-t">
                    <div className="bg-green-50 p-4 rounded-lg flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Kòmand Achte sou Amazon!</p>
                        {selectedOrder.amazon_order_id && (
                          <p className="text-xs text-green-600">Amazon ID: {selectedOrder.amazon_order_id}</p>
                        )}
                        {selectedOrder.purchased_at && (
                          <p className="text-xs text-green-600">
                            Achte: {new Date(selectedOrder.purchased_at).toLocaleDateString('ht-HT')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Mark Complete Button for SUPER_ADMIN (after payment) - Legacy */}
                {canMarkComplete(selectedOrder) && selectedOrder.status !== 'sent_to_amazon' && !canAddToAmazonCart(selectedOrder) && (
                  <div className="pt-4 border-t">
                    <div className="bg-green-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-green-800 font-medium">
                        Peman resevwa. Pase kòmand la sou Amazon epi make li konplete.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        const amazonId = prompt("Antre Amazon Order ID (opsyonèl):");
                        handleMarkComplete(selectedOrder.id, amazonId || "");
                      }}
                      className="w-full bg-[#FF9900] hover:bg-[#E88B00] text-white py-6 text-lg font-bold"
                      data-testid="mark-complete-btn"
                    >
                      <Package className="w-5 h-5 mr-2" />
                      Make Konplete (Fin Pase sou Amazon)
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {selectedOrder.status === 'completed' && (
                  <div className="pt-4 border-t">
                    <div className="bg-green-50 p-4 rounded-lg flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Kòmand Konplete!</p>
                        {selectedOrder.amazon_order_id && (
                          <p className="text-xs text-green-600">Amazon ID: {selectedOrder.amazon_order_id}</p>
                        )}
                        {selectedOrder.completed_at && (
                          <p className="text-xs text-green-600">
                            Konplete: {new Date(selectedOrder.completed_at).toLocaleDateString('ht-HT')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete and Close Buttons */}
                <div className="flex gap-3 pt-4">
                  {canDelete(selectedOrder) && (
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteOrder(selectedOrder)}
                      className="flex-1"
                      data-testid="delete-order-modal"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Efase Kòmand
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setSelectedOrder(null)}
                    className={canDelete(selectedOrder) ? "flex-1" : "w-full"}
                  >
                    Fèmen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payment Modal */}
        {paymentOrder && (
          <PaymentModal
            order={paymentOrder}
            onClose={() => setPaymentOrder(null)}
            onSuccess={() => {
              fetchOrders();
              setPaymentOrder(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDeleteOrder(null)}>
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Efase Kòmand?</h3>
                  <p className="text-gray-600 mb-6">
                    Ou sèten ou vle efase kòmand <span className="font-bold text-gray-900">{deleteOrder.order_number}</span>?
                    <br />
                    <span className="text-red-600 text-sm">Aksyon sa a pa ka defèt.</span>
                  </p>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteOrder(null)}
                      className="flex-1"
                    >
                      Anile
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(deleteOrder.id, deleteOrder.order_number)}
                      className="flex-1"
                      data-testid="confirm-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Wi, Efase
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ASIN Modal */}
        {showAsinModal && asinOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAsinModal(false)}>
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="border-b sticky top-0 bg-white z-10">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-[#FF9900]" />
                  Ajoute ASIN pou Atik yo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-orange-50 p-4 rounded-lg mb-6">
                  <p className="text-sm text-orange-800">
                    <strong>ASIN</strong> se Amazon Standard Identification Number.
                    Ou ka jwenn li nan URL pwodwi Amazon a oswa nan paj pwodwi a.
                  </p>
                  <p className="text-xs text-orange-600 mt-2">
                    Egzanp URL: amazon.com/dp/<strong className="text-orange-800">B08XVYZ1Y5</strong>/
                  </p>
                </div>

                <div className="space-y-4">
                  {(asinOrder.items || []).map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-500">Kantite: {item.quantity || 1} × ${item.unit_price}</p>
                        </div>
                        <span className="text-lg font-bold text-[#FF9900]">${item.total || (item.quantity * item.unit_price)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 w-16">ASIN:</label>
                        <input
                          type="text"
                          value={itemAsins[index] || ''}
                          onChange={(e) => setItemAsins({ ...itemAsins, [index]: e.target.value.toUpperCase() })}
                          placeholder="Egz: B08XVYZ1Y5"
                          className="flex-1 border rounded-lg px-3 py-2 font-mono uppercase"
                          maxLength={10}
                        />
                        <a
                          href={`https://www.amazon.com/s?k=${encodeURIComponent(item.name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline whitespace-nowrap"
                        >
                          Chèche sou Amazon
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAsinModal(false)}
                    className="flex-1"
                  >
                    Anile
                  </Button>
                  <Button
                    onClick={handleSaveAsinsAndProceed}
                    className="flex-1 bg-[#FF9900] hover:bg-[#E88B00] text-black font-bold"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Kontinye - Ajoute nan Cart
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

